/**
 * A sprite unit's animation state (integration plan slice 3): which anim it
 * plays, in which drawn facing, and when that anim started on the unit
 * layer's clock (ms of frame time scaled by speed; `UnitLayer.now`). The frame
 * shown is always the clock now minus that start, so an anim started between
 * two frames starts at its own start, never at the previous frame's time: an
 * `attack` reaches its `hitFrame` exactly at the sum of the `ms` before it,
 * when its event resolves. The anim is a **one-shot** over a **base**:
 *
 * - one-shots: `attack` (`attacked`), `cast` (`skill_used`, `charge_fired`),
 *   `hurt` (`damaged`). They play once; the event resolves at the one-shot's
 *   `hitFrame` (`events.ts`), and the recovery frames finish on their own while
 *   the next event plays.
 * - the base, from the pose: `faint` while down (not looping, its last frame
 *   held), `sleep` while asleep, `charge` while holding a charge, `walk` while a
 *   move's tween runs (`run` while a scene walker runs, `walk` where the sheet
 *   has no `run`; slice 6a) (its phase carries on when the next move starts within
 *   `presentation.sprite.walkResumeMs`, and starts over after), else `idle`.
 *
 * An anim the sheet does not have falls back to `idle` in the same facing
 * (`sprites.ts`). A fainted unit whose sheet has no `faint` (Brad's
 * placeholder draws only idle, walk and run) holds its idle frame 0, dimmed by
 * `presentation.sprite.missingFaintFade` (`sprite-layer.ts`), with the
 * gesture's flash, and is not animating (it does not keep Off's idle frames
 * running); a sprite is never rotated or scaled, so the proxy's lying pose
 * does not apply. That goes away when Brad's combat frames are drawn. At
 * speed 0 a one-shot does not start and a held anim shows its last frame.
 */
import { presentation } from './constants.ts';
import type { Resolved, SpriteSheet } from './sprites.ts';
import type { UnitPose } from './units.ts';

export type SpriteAnimState = {
  /** A one-shot playing over the base, or null. */
  oneShot: string | null;
  /** The anim the clock runs for, and when it started on the layer's clock (ms); -Infinity holds its last frame. */
  current: string;
  startedAt: number;
  /** The walk cycle's phase when the unit last stopped walking, and when (layer clock, ms). */
  walkPhase: number;
  stoppedAt: number;
};

export const freshAnim = (down: boolean, now: number): SpriteAnimState => ({
  oneShot: null,
  current: down ? 'faint' : 'idle',
  startedAt: down ? Number.NEGATIVE_INFINITY : now,
  walkPhase: 0,
  stoppedAt: Number.NEGATIVE_INFINITY,
});

/** The base anim a pose asks for. */
export const baseAnim = (p: UnitPose): string => (p.down ? 'faint' : p.sleeping ? 'sleep' : p.charging ? 'charge' : p.walking ? (p.running ? 'run' : 'walk') : 'idle');

/**
 * Starts a one-shot at `now` (the layer's clock); returns the ms from its start to its `hitFrame` (or its end,
 * without one); 0 when the sheet does not draw it, the unit is down, or at speed 0 (`instant`: nothing to show).
 */
export const startOneShot = (p: UnitPose, sheet: SpriteSheet, name: string, now: number, instant: boolean): number => {
  if (!sheet.has(name) || p.down || instant) return 0;
  const run = sheet.resolve(name, p.facing).run;
  p.anim.oneShot = name;
  p.anim.current = name;
  p.anim.startedAt = now;
  const upTo = run.hitFrame ?? run.frames.length;
  return run.ms.slice(0, upTo).reduce((a, b) => a + b, 0);
};

/** Moves a pose to the anim it should play at `now` (the layer's clock); speed 0 (`instant`) finishes what does not loop. */
export const advance = (p: UnitPose, sheet: SpriteSheet, now: number, instant: boolean): void => {
  const s = p.anim;
  const asked = s.oneShot ?? baseAnim(p);
  // A sheet without `run` runs with its walk cycle (a scene walker; slice 6a).
  const want = asked === 'run' && !sheet.has('run') ? 'walk' : asked;
  if (want !== s.current) {
    if (s.current === 'walk' && Number.isFinite(s.startedAt)) {
      s.walkPhase = now - s.startedAt;
      s.stoppedAt = now;
    }
    s.startedAt = want === 'walk' && now - s.stoppedAt <= presentation.sprite.walkResumeMs ? now - s.walkPhase : now;
    s.current = want;
  }
  const run = sheet.resolve(s.current, p.facing).run;
  if (instant && !run.loop) s.startedAt = Number.NEGATIVE_INFINITY;
  if (s.oneShot !== null && now - s.startedAt >= run.total) {
    s.oneShot = null;
    advance(p, sheet, now, instant);
  }
};

/** Whether the frame is the missing-faint hold: down, and the sheet draws no `faint` (idle frame 0, still). */
const heldIdle = (p: UnitPose, resolved: Resolved): boolean => p.anim.current === 'faint' && resolved.name !== 'faint';

/** Whether a walk or run cycle follows the walker's distance (a scene's walker, `cycle` set) rather than the clock. */
const strideDriven = (p: UnitPose, resolved: Resolved): boolean => p.cycle !== undefined && p.anim.oneShot === null && (resolved.name === 'walk' || resolved.name === 'run') && resolved.run.loop;

/** The frame a pose shows at `now`: the resolved anim and the index into its frames. */
export const frameOf = (p: UnitPose, sheet: SpriteSheet, now: number): { readonly resolved: Resolved; readonly index: number } => {
  const resolved = sheet.resolve(p.anim.current, p.facing);
  if (heldIdle(p, resolved)) return { resolved, index: 0 };
  const { run } = resolved;
  const last = run.frames.length - 1;
  // A scene walker's stride: the frame its distance walked has reached.
  if (strideDriven(p, resolved)) return { resolved, index: Math.min(last, Math.floor((p.cycle ?? 0) * run.frames.length)) };
  let t = now - p.anim.startedAt;
  if (run.loop && Number.isFinite(t)) t %= run.total;
  else if (t >= run.total) return { resolved, index: last };
  for (let i = 0; i <= last; i += 1) {
    t -= run.ms[i] ?? 0;
    if (t < 0) return { resolved, index: i };
  }
  return { resolved, index: last };
};

/** Whether a pose's frame still changes at `now`: a one-shot, a loop of more than one frame, or frames still to come (a stride-driven cycle changes only as the walker moves). */
export const isAnimating = (p: UnitPose, sheet: SpriteSheet, now: number): boolean => {
  if (p.anim.oneShot !== null) return true;
  const resolved = sheet.resolve(p.anim.current, p.facing);
  if (heldIdle(p, resolved) || strideDriven(p, resolved)) return false;
  const { run } = resolved;
  return run.loop ? run.frames.length > 1 : now - p.anim.startedAt < run.total;
};
